/**
 * ============================================================================
 * VELA COMPUTE — CENTRAL BRANDING CONFIGURATION
 * ============================================================================
 * SINGLE SOURCE OF TRUTH for every brand-related value across the app.
 *
 * Rebrand the entire product by editing THIS FILE ONLY.
 * Every page, component, layout, email, notification and future module reads
 * from `BRAND` via `import { BRAND } from "@/branding.config"`.
 *
 * Env overrides (optional): any REACT_APP_BRAND_* variable overrides a default.
 * ============================================================================
 */

const env = (key, fallback) => {
  const v = process.env[key];
  return v !== undefined && v !== "" ? v : fallback;
};

const currentYear = new Date().getFullYear();

export const BRAND = {
  /* -------- Identity -------- */
  app_name:            env("REACT_APP_BRAND_APP_NAME",            "PurpleVPS"),
  company_name:        env("REACT_APP_BRAND_COMPANY_NAME",        "PurpleVPS"),
  short_name:          env("REACT_APP_BRAND_SHORT_NAME",          "Purple"),
  legal_company_name:  env("REACT_APP_BRAND_LEGAL_NAME",          "PurpleVPS Ltd."),
  tagline:             env("REACT_APP_BRAND_TAGLINE",             "The VPS your algorithms deserve."),
  founding_year:       parseInt(env("REACT_APP_BRAND_FOUNDING_YEAR", "2024"), 10),

  /* -------- Wordmark (logo text split into 2 tone-styled tokens) -------- */
  logo_text: {
    first:  env("REACT_APP_BRAND_LOGO_FIRST",  "Purple"),
    second: env("REACT_APP_BRAND_LOGO_SECOND", "VPS"),
  },

  /* -------- Assets -------- */
  logo:       env("REACT_APP_BRAND_LOGO",       "/branding/logo.svg"),
  logo_dark:  env("REACT_APP_BRAND_LOGO_DARK",  "/branding/logo-dark.svg"),
  logo_light: env("REACT_APP_BRAND_LOGO_LIGHT", "/branding/logo-light.svg"),
  favicon:    env("REACT_APP_BRAND_FAVICON",    "/branding/favicon.svg"),
  default_og_image: env("REACT_APP_BRAND_OG_IMAGE", "/branding/og-image.png"),

  /* -------- Colors (HEX). Also mapped to CSS variables at boot. -------- */
  primary_color:   env("REACT_APP_BRAND_PRIMARY_COLOR",   "#7C3AED"),
  secondary_color: env("REACT_APP_BRAND_SECONDARY_COLOR", "#9333EA"),
  accent_color:    env("REACT_APP_BRAND_ACCENT_COLOR",    "#EC4899"),

  /* -------- Contact & addresses -------- */
  support_email:  env("REACT_APP_BRAND_SUPPORT_EMAIL", "support@purplevps.com"),
  sales_email:    env("REACT_APP_BRAND_SALES_EMAIL",   "sales@purplevps.com"),
  billing_email:  env("REACT_APP_BRAND_BILLING_EMAIL", "billing@purplevps.com"),
  contact_email:  env("REACT_APP_BRAND_CONTACT_EMAIL", "hello@purplevps.com"),
  phone_number:   env("REACT_APP_BRAND_PHONE",         "+41 44 000 00 00"),
  company_address: env("REACT_APP_BRAND_ADDRESS",       "Zurich, Switzerland"),

  /* -------- Web -------- */
  website_url: env("REACT_APP_BRAND_WEBSITE_URL", "https://purplevps.com"),

  /* -------- Meta / SEO -------- */
  default_meta_title:       env(
    "REACT_APP_BRAND_META_TITLE",
    "PurpleVPS — Premium Forex & Windows VPS",
  ),
  default_meta_description: env(
    "REACT_APP_BRAND_META_DESCRIPTION",
    "Ultra-low-latency Forex and Windows VPS. Deployed in under 60 seconds from 12 tier-3 regions worldwide, engineered by trading-infrastructure veterans.",
  ),

  /* -------- Social -------- */
  social_links: {
    twitter:  env("REACT_APP_BRAND_SOCIAL_TWITTER",  "https://twitter.com/purplevps"),
    github:   env("REACT_APP_BRAND_SOCIAL_GITHUB",   "https://github.com/purplevps"),
    linkedin: env("REACT_APP_BRAND_SOCIAL_LINKEDIN", "https://linkedin.com/company/purplevps"),
    discord:  env("REACT_APP_BRAND_SOCIAL_DISCORD",  ""),
    youtube:  env("REACT_APP_BRAND_SOCIAL_YOUTUBE",  ""),
  },

  /* -------- Compliance badges (footer strip) -------- */
  compliance_badges: (env("REACT_APP_BRAND_COMPLIANCE", "SOC 2,ISO 27001,GDPR") || "")
    .split(",").map((s) => s.trim()).filter(Boolean),

  /* -------- Product-specific navigation labels (used by nav/footer) -------- */
  product_labels: {
    portal:  "Client portal",
    admin:   "Admin console",
  },
};

BRAND.copyright_text = `© ${currentYear} ${BRAND.legal_company_name.replace(/\.$/, "")}. All rights reserved.`;

/**
 * Convenience getter used inside JSX for the two-tone wordmark:
 *   <>{brandName().first}<span className="brand-gradient-text">{brandName().second}</span></>
 */
export const brandName = () => BRAND.logo_text;

/**
 * Return a mailto: string honoring configured email addresses.
 */
export const mailto = (kind = "contact_email", subject) => {
  const addr = BRAND[kind] || BRAND.contact_email;
  const q = subject ? `?subject=${encodeURIComponent(subject)}` : "";
  return `mailto:${addr}${q}`;
};

/**
 * All social links that are configured (not blank). Icon key is a string;
 * consumers map the key to their icon component.
 */
export const activeSocialLinks = () =>
  Object.entries(BRAND.social_links)
    .filter(([, url]) => !!url)
    .map(([key, url]) => ({ key, url }));

export default BRAND;
